// hardcoded-ui-text: allow — a developer-only panel: RealtimeChat.tsx mounts it behind `showDebug` and NODE_ENV === 'development'.
// It is not part of the product a customer or an operator sees, so its copy stays in
// English. Checked at the call site, not assumed from the folder name.
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { AISuggestionBar } from '@/components/chat/AISuggestionBar';
import { useLogging } from '@/shared/hooks/useLogging';

interface AISuggestion {
  id: string;
  content: string;
  confidence: number;
  aiSource: {
    modelUsed: string;
    provider: string;
    generatedAt: string;
  };
}

interface AISuggestionTesterProps {
  conversationId: string;
  customerId: string;
  customerPhone?: string;
  currentSuggestion: AISuggestion | null;
  loading: boolean;
  generateAISuggestion: (message: string) => Promise<unknown>;
  clearSuggestion: () => void;
  triggerSuggestionForUnresponded?: (message: string) => Promise<unknown>;
}

export function AISuggestionTester({ 
  conversationId, 
  customerId, 
  customerPhone,
  currentSuggestion,
  loading,
  generateAISuggestion,
  clearSuggestion,
  triggerSuggestionForUnresponded
}: AISuggestionTesterProps) {
  const { debug, error } = useLogging('ai');

  const testMessage = "Si agenda me una revisión";

  const handleTestSuggestion = () => {
    debug('Manual Test: Triggering AI suggestion for test message');
    generateAISuggestion(testMessage);
  };

  const handleTestUnresponded = async () => {
    debug('Button clicked - handleTestUnresponded', {
      hasHook: !!triggerSuggestionForUnresponded,
      hookType: typeof triggerSuggestionForUnresponded,
      conversationId,
      customerId,
      customerPhone,
      testMessage,
      loading,
      currentSuggestion
    });
    
    if (!triggerSuggestionForUnresponded) {
      debug('triggerSuggestionForUnresponded not available');
      return;
    }
    
    try {
      debug('About to call triggerSuggestionForUnresponded');
      const result = await triggerSuggestionForUnresponded(testMessage);
      debug('Function call completed with result:', { result });
    } catch (err) {
      error('Error in handleTestUnresponded', err);
    }
  };

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-sm">AI Suggestion Tester</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <span className="text-sm">Status:</span>
          {loading && <Badge variant="secondary">Loading...</Badge>}
          {currentSuggestion && <Badge variant="default">Has Suggestion</Badge>}
          {!loading && !currentSuggestion && <Badge variant="outline">Ready</Badge>}
        </div>

        <div className="flex gap-2">
          <Button 
            size="sm" 
            onClick={handleTestSuggestion}
            disabled={loading}
          >
            Test Direct Suggestion
          </Button>
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleTestUnresponded}
            disabled={loading || !triggerSuggestionForUnresponded}
          >
            Test Unresponded Trigger
          </Button>
          <Button 
            size="sm" 
            variant="destructive"
            onClick={clearSuggestion}
            disabled={!currentSuggestion}
          >
            Clear Suggestion
          </Button>
        </div>

        {currentSuggestion && (
          <div className="text-xs text-muted-foreground p-2 bg-secondary/50 rounded">
            <strong>Current Suggestion:</strong> {currentSuggestion.content.substring(0, 100)}...
            <br />
            <strong>Confidence:</strong> {Math.round(currentSuggestion.confidence * 100)}%
          </div>
        )}
      </CardContent>
    </Card>
  );
}